import { useMemo } from 'react';

import { isInComposeMode } from '@/config/office/office';
import {
  type Suggestion,
  SuggestionCards,
  SuggestionCardsSkeleton,
} from '@/features/assistant/components/SuggestionCards';
import { DEFAULT_SUGGESTIONS } from '@/utils/constants';
import { useOfficeContextStore } from '@/utils/stores/useOfficeContextStore';

import { useSuggestions } from '../hooks/useSuggestions';

interface SmartSuggestionsProps {
  isVisible: boolean;
  onSelect: (suggestion: Suggestion) => void;
  sessionId: string | undefined;
}

type SuggestionsScope =
  | { messageId?: string; scope: 'EMAIL' | 'INBOX' }
  | { conversationId?: string; scope: 'THREAD' };

export function SmartSuggestions({ isVisible, onSelect, sessionId }: SmartSuggestionsProps) {
  const { activeSessionContext, contextInfo, getCurrentFilterMode } = useOfficeContextStore();

  const suggestionsScope = useMemo<SuggestionsScope>(() => {
    if (isInComposeMode()) {
      return { scope: 'INBOX' };
    }

    const filterMode = getCurrentFilterMode();
    switch (filterMode) {
      case 'currentEmailThread':
        if (activeSessionContext?.context === 'EMAIL' || !contextInfo.conversationId) {
          return { scope: 'EMAIL', ...(contextInfo.itemId && { messageId: contextInfo.itemId }) };
        }
        return {
          conversationId: contextInfo.conversationId,
          scope: 'THREAD',
        };
      default:
        return { scope: 'INBOX', ...(contextInfo.itemId && { messageId: contextInfo.itemId }) };
    }
  }, [
    activeSessionContext?.context,
    contextInfo.conversationId,
    contextInfo.itemId,
    getCurrentFilterMode,
  ]);

  const shouldFetch = isVisible && !sessionId;

  const suggestionsQuery = useSuggestions({
    scope: suggestionsScope.scope,
    ...(sessionId && { sessionId }),
    ...((suggestionsScope.scope === 'EMAIL' || suggestionsScope.scope === 'INBOX') &&
      'messageId' in suggestionsScope &&
      suggestionsScope.messageId && { messageId: suggestionsScope.messageId }),
    ...(suggestionsScope.scope === 'THREAD' &&
      'conversationId' in suggestionsScope &&
      suggestionsScope.conversationId && { conversationId: suggestionsScope.conversationId }),
    enabled: shouldFetch,
  });

  const smartSuggestions: Array<Suggestion> = suggestionsQuery.data?.data
    ? suggestionsQuery.data.data.map((title: string, index: number) => ({
        icon: 'checklist' as const,
        id: `smart-suggestion-${index}`,
        title,
      }))
    : [];

  const displaySuggestions = smartSuggestions.length > 0 ? smartSuggestions : DEFAULT_SUGGESTIONS;

  return (
    <div className="flex flex-col gap-2">
      {suggestionsQuery.isLoading ? (
        <SuggestionCardsSkeleton count={3} />
      ) : (
        <SuggestionCards onSelect={onSelect} suggestions={displaySuggestions} />
      )}
    </div>
  );
}
